class JournalBlog extends HTMLElement {
  connectedCallback() {
    this.featured = this.querySelector('[data-journal-featured]');
    this.more = this.querySelector('[data-journal-more]');
    this.grid = this.querySelector('[data-journal-grid]');
    this.pagination = this.querySelector('[data-journal-pagination]');
    this.status = this.querySelector('[data-journal-page-status]');
    this.source = Array.from(this.querySelectorAll('[data-journal-source] [data-journal-card]'));
    this.currentPage = 1;

    if (!this.featured || !this.grid || !this.source.length) return;

    this.cards = this.source
      .map((card) => card.cloneNode(true))
      .sort((a, b) => Number(b.dataset.published || 0) - Number(a.dataset.published || 0));

    this.featureCards = this.cards.slice(0, 3);
    this.gridCards = this.cards.slice(3);

    this.renderFeatured();
    this.renderGrid();
    this.initializeScrollAnimation();

    this.onResize = () => {
      const pageSize = this.pageSize;
      if (pageSize !== this.lastPageSize) {
        this.lastPageSize = pageSize;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
        this.renderGrid(false);
      }
    };

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  get pageSize() {
    return window.matchMedia('(min-width: 750px)').matches ? 8 : 4;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.gridCards.length / this.pageSize));
  }

  renderFeatured() {
    this.featured.replaceChildren();

    this.featureCards.forEach((card, index) => {
      const item = card.cloneNode(true);
      item.classList.add('journal-card--featured');
      this.prepareAnimatedCard(item, index);
      if (index === 0) {
        item.classList.add('journal-card--hero');
        item.querySelectorAll('[data-journal-eyebrow]').forEach((eyebrow) => {
          eyebrow.textContent = eyebrow.dataset.latestLabel || eyebrow.textContent;
        });
      }
      this.featured.append(item);
    });
  }

  renderGrid(animate = true) {
    const totalPages = this.totalPages;
    this.currentPage = Math.min(Math.max(this.currentPage, 1), totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    const pageCards = this.gridCards.slice(start, start + this.pageSize);

    this.grid.replaceChildren();
    pageCards.forEach((card, index) => {
      const item = card.cloneNode(true);
      item.classList.add('journal-card--grid');
      if (animate) this.prepareAnimatedCard(item, start + index);
      item.querySelectorAll('[data-journal-cta]').forEach((cta) => {
        cta.textContent = 'Read more';
      });
      this.grid.append(item);
    });

    this.renderPagination(totalPages);
    if (animate) this.initializeScrollAnimation();
  }

  prepareAnimatedCard(card, index) {
    card.classList.add('scroll-trigger', 'animate--slide-in');
    card.setAttribute('data-cascade', '');
    card.style.setProperty('--animation-order', index + 1);
  }

  initializeScrollAnimation() {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      window.requestAnimationFrame(() => initializeScrollAnimationTrigger(this));
    }
  }

  renderPagination(totalPages) {
    if (!this.pagination) return;

    this.pagination.hidden = totalPages <= 1;
    this.pagination.replaceChildren();

    if (totalPages <= 1) return;

    const previous = this.createButton('Previous', this.currentPage - 1, this.currentPage === 1);
    const next = this.createButton('Next', this.currentPage + 1, this.currentPage === totalPages);
    const pages = document.createElement('div');
    pages.className = 'journal-pagination__pages';

    for (let page = 1; page <= totalPages; page += 1) {
      const button = this.createButton(String(page), page, false);
      button.setAttribute('aria-current', page === this.currentPage ? 'page' : 'false');
      pages.append(button);
    }

    this.pagination.append(previous, pages, next);

    if (this.status) {
      this.status.textContent = `Page ${this.currentPage} of ${totalPages}`;
    }
  }

  createButton(label, page, disabled) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'journal-pagination__button';
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener('click', () => {
      this.currentPage = page;
      this.renderGrid(false);
      (this.more || this.grid).scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    return button;
  }
}

customElements.define('journal-blog', JournalBlog);
